import { StreamId } from "@common/domain";
import { combineLatest, EMPTY, merge, Subject } from "rxjs";
import { map, switchMap, takeUntil, tap } from "rxjs/operators";
import {
  CanvasedExtendedMediaStream,
  ExtendedMediaStream
} from "../../extended-media-stream";
import { LocalConferenceStream } from "./LocalConferenceStream";

// 'LocalCamMicConferenceStream' has it's own instance of a 'stream',
// that would be published.
//
// 'LocalCamMicConferenceStream' doesn't own 'newStream' tracks,
// it's up to the client to stop them,
// it would only call 'removeTrack' for each track on it's own Stream during destroy.

export class LocalCamMicConferenceStream extends LocalConferenceStream {
  private readonly _newStream$ = new Subject<null | ExtendedMediaStream>();
  private readonly _destroy1$ = new Subject<void>();

  constructor(
    streamId: StreamId,
    stream: ExtendedMediaStream,
    logger: (...args: unknown[]) => void
  ) {
    super(streamId, logger);

    this._stream$.next(stream);

    combineLatest([this._newStream$, this._stream$])
      .pipe(
        map((_) => ({ newStream: _[0], stream: _[1] })),
        switchMap((_) =>
          _.newStream && _.stream
            ? this.mapTracks(_.newStream, _.stream)
            : EMPTY
        ),
        takeUntil(this._destroy1$)
      )
      .subscribe();
  }

  get isCanvased() {
    return this._stream$.value instanceof CanvasedExtendedMediaStream;
  }

  get isCanvased$() {
    return this._stream$.pipe(
      map((_) => _ instanceof CanvasedExtendedMediaStream)
    );
  }

  override async audioDisable() {
    await super.audioDisable();
  }

  override async audioEnable() {
    await super.audioEnable();
  }

  override async videoDisable() {
    await super.videoDisable();
  }

  override async videoEnable() {
    await super.videoEnable();
  }

  override async destroy() {
    this._destroy1$.next();
    this._destroy1$.complete();

    // 'LocalCamMicConferenceStream' instance doesn't own tracks,
    // just release them
    const stream = this._stream$.value;
    stream?.tracks.forEach((_) => stream.removeTrack(_));

    await super.destroy();
  }

  update(newStream: ExtendedMediaStream) {
    this._newStream$.next(newStream);
  }

  // Update 'stream' with the tracks from the 'newStream'.
  // Track the newStream's tracks and sync them to the 'stream'.
  private mapTracks(
    newStream: ExtendedMediaStream,
    stream: ExtendedMediaStream
  ) {
    stream.tracks.forEach((_) => stream.removeTrack(_));
    newStream.tracks.forEach((_) => stream.addTrack(_));

    return merge(
      newStream.addTrack$.pipe(map((_) => ({ addtrack: _ }))),
      newStream.removeTrack$.pipe(map((_) => ({ removetrack: _ })))
    ).pipe(
      tap((_) =>
        "addtrack" in _
          ? stream.addTrack(_.addtrack)
          : stream.removeTrack(_.removetrack)
      )
    );
  }
}
